import { PermissionGuard } from "@/components/PermissionGuard";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { BookCheck, ExternalLink, RefreshCw, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useDolfinAuth, useDolfinClient, type DolfinClient } from "@/components/DolfinProvider";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { apiErrorMessage } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-public";

export type AccountingSync = components["schemas"]["AccountingDocumentSyncResponse"];

const PLATFORM_NAMES: Record<string, string> = { xero: "Xero", quickbooks: "QuickBooks" };

export function platformName(platform?: string | null) {
  if (!platform) return "your accounting software";
  return PLATFORM_NAMES[platform] ?? platform.charAt(0).toUpperCase() + platform.slice(1);
}

// One read per organisation however many badges a table renders.
const connectionNames = new Map<string, Promise<string | null>>();

/**
 * The connected platform's name ("Xero"), so push copy never says "books": that word is Dolfin's own ledger.
 * Falls back to "your accounting software" until known, or with no connection.
 */
export function useAccountingPlatformName(): string {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    if (!connectionNames.has(organisationId)) connectionNames.set(organisationId, readName(api));
    void connectionNames.get(organisationId)!.then((n) => !cancelled && setName(n));
    return () => {
      cancelled = true;
    };
  }, [api, organisationId]);
  return name ?? "your accounting software";
}

async function readName(api: DolfinClient): Promise<string | null> {
  const { data } = await api.GET("/v1/accounting/connection", {});
  return data?.status === "Callable" ? (data.serviceDisplayName ?? null) : null;
}

// Same vocabulary on invoices, payments and credit notes, so a missing row reads the same everywhere.
export function AccountingSyncBadge({
  sync,
  className,
  waitsFor = "invoice",
}: {
  sync?: AccountingSync | null;
  className?: string;
  /** What a Waiting row waits on, for the hint: its invoice or its bill. */
  waitsFor?: string;
}) {
  const connected = useAccountingPlatformName();
  const tone = (() => {
    switch (sync?.status) {
      case "Synced":
        return { dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", label: `In ${platformName(sync.platform)}` };
      // Pushed once and no longer right (a reversed credit, a payment the bank failed): a person acts in the platform.
      case "Failed":
        return sync.syncedAt
          ? { dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", label: `Action needed in ${platformName(sync.platform)}` }
          : { dot: "bg-destructive", text: "text-destructive", label: "Not pushed" };
      case "Pending":
        return { dot: "bg-sky-500 animate-pulse", text: "text-sky-700 dark:text-sky-400", label: "Pushing" };
      case "Waiting":
        return { dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", label: "Waiting" };
      case "Skipped":
        return { dot: "bg-muted-foreground/60", text: "text-muted-foreground", label: "Not pushed" };
      default:
        return { dot: "bg-muted-foreground/40", text: "text-muted-foreground", label: `Not in ${sync?.platform ? platformName(sync.platform) : connected}` };
    }
  })();

  const label = (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap", tone.text, className)}>
      <span className={cn("size-1.5 rounded-full", tone.dot)} />
      {tone.label}
    </span>
  );

  if (sync?.status === "Synced" && sync.url) {
    return (
      <a href={sync.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
        {label}
        <ExternalLink className="size-3 text-muted-foreground" />
      </a>
    );
  }
  // "Waiting" is short so it fits a table column; why it waits is one hover away.
  if (sync?.status === "Waiting") {
    return (
      <span className="inline-flex items-center gap-1">
        {label}
        <InfoHint label="Why it is waiting">
          Goes to {platformName(sync.platform)} as soon as its {waitsFor} is there.
        </InfoHint>
      </span>
    );
  }
  if ((sync?.status === "Failed" || sync?.status === "Skipped") && sync.lastError) {
    return (
      <span className="inline-flex items-center gap-1">
        {label}
        <InfoHint label={sync.status === "Failed" && sync.syncedAt ? "What to do" : "Why it was not pushed"}>{sync.lastError}</InfoHint>
      </span>
    );
  }
  return label;
}

export function InvoiceAccountingSyncCard({
  invoiceId,
  sync,
  status,
  onRefresh,
}: {
  invoiceId: string;
  sync?: AccountingSync | null;
  status?: string | null;
  onRefresh: () => Promise<void>;
}) {
  const api = useDolfinClient();
  if (!status || status === "Draft") return null;
  return (
    <DocumentAccountingSyncCard
      sync={sync}
      // A void invoice that never reached the books has nothing to push, and the server refuses it.
      canPush={!(status === "Void" && sync?.status !== "Synced")}
      push={() => api.POST("/v1/invoices/{id}/accounting/push", { params: { path: { id: invoiceId } } })}
      failedMessage="Couldn't push the invoice to your accounting software."
      hint="Issued invoices are pushed to your accounting software once an income account is chosen in Organisation, Accounting. Payments, credit notes and a void follow the invoice there. Nothing already there is edited."
      notYet="Not pushed yet. Invoices issued before your accounting software was connected are pushed by hand."
      synced={`In ${platformName(sync?.platform)}. Payments and credit notes recorded here follow it automatically.`}
      onRefresh={onRefresh}
    />
  );
}

// The push runs in the background, so after asking for it the card reloads until the attempt has settled.
export function DocumentAccountingSyncCard({
  sync,
  canPush,
  push,
  failedMessage,
  hint,
  notYet,
  synced,
  titleClassName = "text-base",
  onRefresh,
  children,
}: {
  sync?: AccountingSync | null;
  canPush: boolean;
  push: () => Promise<{ error?: unknown }>;
  failedMessage: string;
  hint: string;
  notYet: string;
  synced: string;
  titleClassName?: string;
  onRefresh: () => Promise<void>;
  /** Further rows under the document's own, such as its payment. */
  children?: ReactNode;
}) {
  const [pushing, setPushing] = useState(false);
  const connected = useAccountingPlatformName();
  const platform = sync?.platform ? platformName(sync.platform) : connected;

  // The latest sync as rendered, for the wait below to read between reloads.
  const latest = useRef(sync);
  latest.current = sync;

  async function handlePush() {
    setPushing(true);
    const { error } = await push();
    if (error) {
      toast.error(apiErrorMessage(error, failedMessage));
      setPushing(false);
      return;
    }
    toast.success(`Pushing to ${platform}.`);
    // A live platform takes a few seconds: reload until this attempt has been made and has settled. An invoice
    // already in the books makes no new attempt of its own (only what follows it does), so one reload is enough.
    const before = latest.current?.lastAttemptAt;
    const wasSynced = latest.current?.status === "Synced";
    const deadline = Date.now() + 30_000;
    do {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await onRefresh();
    } while (
      !wasSynced &&
      Date.now() < deadline &&
      (latest.current?.lastAttemptAt === before || latest.current?.status === "Pending")
    );
    setPushing(false);
  }

  const action = sync?.status === "Synced" ? "Push again" : sync?.status === "Failed" ? "Retry" : `Push to ${platform}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className={cn("flex items-center gap-2", titleClassName)}>
          Accounting
          <InfoHint label="More about accounting sync">{hint}</InfoHint>
        </CardTitle>
        {canPush ? (
          <Button size="sm" variant="outline" disabled={pushing || sync?.status === "Pending"} onClick={handlePush}>
            {pushing ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} {action}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AccountingSyncBadge sync={sync} className="text-sm" />
          {sync?.syncedAt ? (
            <span className="text-xs text-muted-foreground">{formatDateTime(sync.syncedAt)}</span>
          ) : sync?.lastAttemptAt ? (
            <span className="text-xs text-muted-foreground">Last tried {formatDateTime(sync.lastAttemptAt)}</span>
          ) : null}
        </div>
        {sync?.status === "Failed" && sync.lastError ? (
          <p className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
            <TriangleAlert className="mt-px size-3.5 shrink-0" />
            <span>{sync.lastError}</span>
          </p>
        ) : null}
        {!sync ? (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <BookCheck className="mt-px size-3.5 shrink-0" />
            {notYet}
          </p>
        ) : sync.status === "Synced" ? (
          <p className="text-xs text-muted-foreground">{synced}</p>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

// Uploaded separately from the bill, so a failed upload leaves it in the books without its PDF; the next push retries.
export function DocumentAttachmentBadge({ sync }: { sync?: AccountingSync | null }) {
  if (!sync) return null;
  const tone =
    sync.status === "Synced"
      ? { dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", label: "PDF attached" }
      : sync.status === "Pending" || sync.status === "Waiting"
        ? { dot: "bg-sky-500", text: "text-sky-700 dark:text-sky-400", label: "Attaching PDF" }
        : sync.status === "Skipped"
          ? { dot: "bg-muted-foreground/60", text: "text-muted-foreground", label: "No PDF to attach" }
          : { dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", label: "PDF not attached" };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium whitespace-nowrap", tone.text)}>
        <span className={cn("size-1.5 rounded-full", tone.dot)} />
        {tone.label}
      </span>
      {sync.lastError && sync.status !== "Synced" ? (
        <InfoHint label="Why the PDF is not attached">{sync.lastError}</InfoHint>
      ) : null}
    </span>
  );
}
